/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Select } from 'antd'
import { FC, useState, useEffect } from 'react'
import { CustomFormInstance, ValueOf } from '../../../../types'
import { FormValues, MachineForm, MachineNames, MachineValues } from '../types'
import { extractComponentName } from '../../../functions'
import { connectionRelation, UNITS } from '../../../../const'

type Props = {
    form: CustomFormInstance<MachineForm>
    name: MachineNames.featuresPartsOfIndependentClaim
}

const FeaturesPartsOfIndependentClaim: FC<Props> = ({ form, name }) => {
    type Option = {
        value: string
        label: string
    }
    const [selectOptions, setSelectOptions] = useState<Option[]>([])
    useEffect(() => {
        const primaryComponents =
            form.getFieldValue<MachineForm['primaryComponents']>(
                'primaryComponents'
            ) ?? []
        const additionalComponents = form.getFieldValue<
            MachineForm['primaryAdditionalComponents']
        >(MachineNames.primaryAdditionalComponents)
        const summaryOfSelectedEmbodimentsOfTheInvention = form.getFieldValue<
            FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
        >(['summaryOfSelectedEmbodimentsOfTheInvention'])
        const partsOfIndependentClaim = form.getFieldValue<
            MachineValues['partsOfIndependentClaim']
        >(['partsOfIndependentClaim'])
        const parentClaimPrimaryComponents = primaryComponents.filter(
            (item) => {
                if (
                    summaryOfSelectedEmbodimentsOfTheInvention &&
                    item !== undefined
                ) {
                    return summaryOfSelectedEmbodimentsOfTheInvention.includes(
                        item.id ?? ''
                    )
                }
                if (partsOfIndependentClaim && item !== undefined) {
                    return partsOfIndependentClaim.includes(item.id ?? '')
                }
                return item !== undefined
            }
        )
        const parentClaimAdditionalComponents = additionalComponents?.filter(
            (item) => {
                if (
                    summaryOfSelectedEmbodimentsOfTheInvention &&
                    item !== undefined
                ) {
                    return summaryOfSelectedEmbodimentsOfTheInvention.includes(
                        `add_${item.id}`
                    )
                }
                if (partsOfIndependentClaim && item !== undefined) {
                    return partsOfIndependentClaim.includes(`add_${item.id}`)
                }
                return false
            }
        )
        const characteristicComponents = form.getFieldValue(
            MachineNames.componentsForCharacteristics
        ) as MachineValues['componentsForCharacteristics']
        characteristicComponents.forEach((component: string) => {
            const foundComponent = parentClaimPrimaryComponents.find(
                (x) => x?.id === component
            )
            if (foundComponent) {
                const characteristic = form.getFieldValue([
                    MachineNames.characteristic,
                    component,
                ]) as ValueOf<MachineValues['characteristic']>
                const additionalCharacteristic = form.getFieldValue<
                    ValueOf<MachineValues['additionalCharacteristic']>
                >(['additionalCharacteristic', component])
                const formatUserDefined = form.getFieldValue<
                    ValueOf<MachineValues['formatUserDefined']>
                >(['formatUserDefined', component])
                if (characteristic) {
                    Object.entries(characteristic).forEach((entity) => {
                        const [entityKey, val] = entity
                        const key = entityKey as keyof typeof UNITS
                        if (val) {
                            if (typeof val === 'object' && val !== null) {
                                if (key === 'defined') {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${
                                                    'description' in val
                                                        ? val.description
                                                        : '__'
                                                }`,
                                                value: `characteristic_${component}.${key}`,
                                            },
                                        ])
                                    )
                                } else {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${foundComponent.component!} has a ${key} from ${
                                                    'from' in val
                                                        ? val.from
                                                        : '__'
                                                } to ${
                                                    'to' in val ? val.to : '__'
                                                } ${UNITS[key]}`,
                                                value: `characteristic_${component}.${key}`,
                                            },
                                        ])
                                    )
                                }
                            }
                        }
                    })
                }
                if (additionalCharacteristic) {
                    Object.entries(additionalCharacteristic).forEach(
                        (entity) => {
                            const [entityKey, val] = entity
                            const key = entityKey as keyof typeof UNITS
                            if (val) {
                                if (typeof val === 'object' && val !== null) {
                                    if ('description' in val) {
                                        if (key === 'defined') {
                                            setSelectOptions((prevState) =>
                                                prevState.concat([
                                                    {
                                                        label: `${
                                                            'description' in val
                                                                ? val.description
                                                                : '__'
                                                        } `,
                                                        value: `additionalCharacteristic_${component}.${key}`,
                                                    },
                                                ])
                                            )
                                        }
                                    } else {
                                        if (key === 'thermalConductivity') {
                                            setSelectOptions((prevState) =>
                                                prevState.concat([
                                                    {
                                                        label: `${
                                                            foundComponent.component
                                                        } has a thermal conductivity from ${
                                                            val.from || '__'
                                                        } to ${
                                                            val.to || '__'
                                                        } ${UNITS[key]}`,
                                                        value: `additionalCharacteristic_${component}.${key}`,
                                                    },
                                                ])
                                            )
                                        }
                                        if (key === 'meltingPoint') {
                                            setSelectOptions((prevState) =>
                                                prevState.concat([
                                                    {
                                                        label: `${
                                                            foundComponent.component
                                                        } has a melting point from ${
                                                            val.from || '__'
                                                        } to ${
                                                            val.to || '__'
                                                        } ${UNITS[key]}`,
                                                        value: `additionalCharacteristic_${component}.${key}`,
                                                    },
                                                ])
                                            )
                                        }
                                        if (key === 'specificHeat') {
                                            setSelectOptions((prevState) =>
                                                prevState.concat([
                                                    {
                                                        label: `${
                                                            foundComponent.component
                                                        } has a specific heat from ${
                                                            val.from || '__'
                                                        } to ${
                                                            val.to || '__'
                                                        } ${UNITS[key]}`,
                                                        value: `additionalCharacteristic_${component}.${key}`,
                                                    },
                                                ])
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    )
                }
                if (formatUserDefined) {
                    Object.entries(formatUserDefined).forEach((entity: any) => {
                        const [entityKey, val] = entity
                        const key = entityKey
                        if (val) {
                            if (typeof val === 'object' && val !== null) {
                                if (key === 'from') {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${foundComponent.component!} has a ${
                                                    val.name || '__'
                                                } from ${
                                                    val.minimum || '__'
                                                } to ${val.maximum || '__'} ${
                                                    val.units || ''
                                                }`,
                                                value: `formatUserDefined_${component}.${key}`,
                                            },
                                        ])
                                    )
                                }
                                if (key === 'equal') {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${
                                                    val.name || '__'
                                                } of the ${foundComponent.component!} is equal to from ${
                                                    val.minimum || '__'
                                                } to ${val.maximum || '__'} ${
                                                    val.units || ''
                                                }`,
                                                value: `formatUserDefined_${component}.${key}`,
                                            },
                                        ])
                                    )
                                }
                                if (key === 'defined') {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${
                                                    val.description || '___'
                                                }`,
                                                value: `formatUserDefined_${component}.${key}`,
                                            },
                                        ])
                                    )
                                }
                            }
                        }
                    })
                }
            }
        })

        if (parentClaimAdditionalComponents) {
            const additionalComponentsForCharacteristics = form.getFieldValue<
                MachineValues['additionalComponentsForCharacteristics']
            >('additionalComponentsForCharacteristics')

            additionalComponentsForCharacteristics?.forEach(
                (component: string) => {
                    const foundComponent =
                        parentClaimAdditionalComponents?.find(
                            (x) => x?.id === component
                        )
                    if (foundComponent) {
                        const addCompCharacteristic = form.getFieldValue([
                            MachineNames.addCompCharacteristic,
                            component,
                        ]) as ValueOf<MachineValues['addCompCharacteristic']>
                        const addCompAdditionalCharacteristic =
                            form.getFieldValue<
                                ValueOf<
                                    MachineValues['addCompAdditionalCharacteristic']
                                >
                            >(['addCompAdditionalCharacteristic', component])
                        const addCompFormatUserDefined = form.getFieldValue<
                            ValueOf<MachineValues['addCompFormatUserDefined']>
                        >(['addCompFormatUserDefined', component])
                        if (addCompCharacteristic) {
                            Object.entries(addCompCharacteristic).forEach(
                                (entity) => {
                                    const [entityKey, val] = entity
                                    const key = entityKey as keyof typeof UNITS
                                    if (val) {
                                        if (
                                            typeof val === 'object' &&
                                            val !== null
                                        ) {
                                            if (key === 'defined') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                'description' in
                                                                val
                                                                    ? val.description
                                                                    : '__'
                                                            }`,
                                                            value: `addCompCharacteristic_${component}.${key}`,
                                                        },
                                                    ])
                                                )
                                            } else {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${foundComponent.component!} has a ${key} from ${
                                                                'from' in val
                                                                    ? val.from
                                                                    : '__'
                                                            } to ${
                                                                'to' in val
                                                                    ? val.to
                                                                    : '__'
                                                            } ${UNITS[key]}`,
                                                            value: `addCompCharacteristic_${component}.${key}`,
                                                        },
                                                    ])
                                                )
                                            }
                                        }
                                    }
                                }
                            )
                        }
                        if (addCompAdditionalCharacteristic) {
                            Object.entries(
                                addCompAdditionalCharacteristic
                            ).forEach((entity) => {
                                const [entityKey, val] = entity
                                const key = entityKey as keyof typeof UNITS
                                if (val) {
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if ('description' in val) {
                                            if (key === 'defined') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                'description' in
                                                                val
                                                                    ? val.description
                                                                    : '__'
                                                            } `,
                                                            value: `addCompAdditionalCharacteristic_${component}.${key}`,
                                                        },
                                                    ])
                                                )
                                            }
                                        } else {
                                            if (key === 'thermalConductivity') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                foundComponent.component
                                                            } has a thermal conductivity from ${
                                                                val.from || '__'
                                                            } to ${
                                                                val.to || '__'
                                                            } ${UNITS[key]}`,
                                                            value: `addCompAdditionalCharacteristic_${component}.${key}`,
                                                        },
                                                    ])
                                                )
                                            }
                                            if (key === 'meltingPoint') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                foundComponent.component
                                                            } has a melting point from ${
                                                                val.from || '__'
                                                            } to ${
                                                                val.to || '__'
                                                            } ${UNITS[key]}`,
                                                            value: `addCompAdditionalCharacteristic_${component}.${key}`,
                                                        },
                                                    ])
                                                )
                                            }
                                            if (key === 'specificHeat') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                foundComponent.component
                                                            } has a specific heat from ${
                                                                val.from || '__'
                                                            } to ${
                                                                val.to || '__'
                                                            } ${UNITS[key]}`,
                                                            value: `addCompAdditionalCharacteristic_${component}.${key}`,
                                                        },
                                                    ])
                                                )
                                            }
                                        }
                                    }
                                }
                            })
                        }
                        if (addCompFormatUserDefined) {
                            Object.entries(addCompFormatUserDefined).forEach(
                                (entity: any) => {
                                    const [entityKey, val] = entity
                                    const key = entityKey
                                    if (val) {
                                        if (
                                            typeof val === 'object' &&
                                            val !== null
                                        ) {
                                            if (key === 'from') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${foundComponent.component!} has a ${
                                                                val.name || '__'
                                                            } from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }`,
                                                            value: `addCompFormatUserDefined_${component}.${key}`,
                                                        },
                                                    ])
                                                )
                                            }
                                            if (key === 'equal') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                val.name || '__'
                                                            } of the ${foundComponent.component!} is equal to from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }`,
                                                            value: `addCompFormatUserDefined_${component}.${key}`,
                                                        },
                                                    ])
                                                )
                                            }
                                            if (key === 'defined') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                val.description ||
                                                                '___'
                                                            }`,
                                                            value: `addCompFormatUserDefined_${component}.${key}`,
                                                        },
                                                    ])
                                                )
                                            }
                                        }
                                    }
                                }
                            )
                        }
                    }
                }
            )
        }
        const importantComponentsConnections = form.getFieldValue<
            MachineValues['importantComponentsConnections']
        >(['importantComponentsConnections'])
        if (importantComponentsConnections != null) {
            Object.entries(importantComponentsConnections).forEach(
                (entry: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const [key, val] = entry
                    let finalText: string | undefined
                    const notSet = val?.notSet
                    const firstComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['1stComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['1stComponent']
                                ))) &&
                        extractComponentName(val['1stComponent'], form)
                    const secondComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['2ndComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['2ndComponent']
                                ))) &&
                        extractComponentName(val['2ndComponent'], form)
                    if (notSet) {
                        const firstPart = val?.['1stPart']
                        const secondPart = val?.['2ndPart']
                        const thirdPart = val?.['3rdPart']
                        if (
                            firstPart &&
                            firstComponent &&
                            secondPart &&
                            secondComponent &&
                            thirdPart
                        ) {
                            finalText = `${firstPart} the ${firstComponent} ${secondPart} the ${secondComponent} ${
                                thirdPart || '__'
                            }`
                        }
                    } else {
                        const foundSubConnectionType = connectionRelation[
                            val?.connectionType
                        ]?.find((connection: any) => {
                            return connection.value === val?.subConnectionType
                        })
                        const connectionType = foundSubConnectionType
                            ? val?.subConnectionType || val?.connectionType
                            : val?.connectionType
                        if (
                            firstComponent &&
                            connectionType &&
                            secondComponent
                        ) {
                            finalText = `${firstComponent} is ${connectionType.toLowerCase()} the ${secondComponent}`
                        }
                    }
                    if (finalText) {
                        setSelectOptions((prevState) =>
                            prevState.concat([
                                {
                                    label: `${finalText}`,
                                    value: `importantComponentsConnections_${val?.id}`,
                                },
                            ])
                        )
                    }
                }
            )
        }
        const importantComponentsArrangements = form.getFieldValue<
            MachineValues['importantComponentsArrangements']
        >(['importantComponentsArrangements'])
        if (importantComponentsArrangements != null) {
            Object.entries(importantComponentsArrangements).forEach(
                (entry: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const [key, val] = entry
                    let finalText: string | undefined
                    const notSet = val?.notSet
                    const firstComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['1stComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['1stComponent']
                                ))) &&
                        extractComponentName(val['1stComponent'], form)
                    const secondComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['2ndComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['2ndComponent']
                                ))) &&
                        extractComponentName(val['2ndComponent'], form)
                    if (notSet) {
                        const firstPart = val?.['1stPart']
                        const secondPart = val?.['2ndPart']
                        const thirdPart = val?.['3rdPart']
                        if (firstComponent && secondPart && secondComponent) {
                            finalText = `${
                                firstPart ? `${firstPart} the ` : ''
                            }${firstComponent} ${secondPart} the ${secondComponent} ${
                                thirdPart || '__'
                            }`
                        }
                    } else {
                        const arrangementType = val?.arrangementType
                        if (
                            firstComponent &&
                            arrangementType &&
                            secondComponent
                        ) {
                            finalText = `${firstComponent} is ${arrangementType.toLowerCase()} the ${secondComponent}`
                        }
                    }
                    if (finalText) {
                        setSelectOptions((prevState) =>
                            prevState.concat([
                                {
                                    label: `${finalText}`,
                                    value: `importantComponentsArrangements_${val?.id}`,
                                },
                            ])
                        )
                    }
                }
            )
        }
        const functionalConnections = form.getFieldValue<
            MachineValues['functionalConnections']
        >(['functionalConnections'])
        if (functionalConnections != null) {
            Object.entries(functionalConnections).forEach((entry: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const [key, val] = entry
                let finalText: string | undefined
                const notSet = val?.notSet
                const firstComponent =
                    val &&
                    ((partsOfIndependentClaim &&
                        partsOfIndependentClaim.includes(
                            val['1stComponent']
                        )) ||
                        (summaryOfSelectedEmbodimentsOfTheInvention &&
                            summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                val['1stComponent']
                            ))) &&
                    extractComponentName(val['1stComponent'], form)
                const secondComponent =
                    val &&
                    ((partsOfIndependentClaim &&
                        partsOfIndependentClaim.includes(
                            val['2ndComponent']
                        )) ||
                        (summaryOfSelectedEmbodimentsOfTheInvention &&
                            summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                val['2ndComponent']
                            ))) &&
                    extractComponentName(val['2ndComponent'], form)
                if (notSet) {
                    const firstPart = val?.['1stPart']
                    const secondPart = val?.['2ndPart']
                    const thirdPart = val?.['3rdPart']
                    if (firstComponent && secondPart && secondComponent) {
                        finalText = `${
                            firstPart ? `${firstPart} the ` : ''
                        }${firstComponent} ${secondPart} the ${secondComponent} ${
                            thirdPart || '__'
                        }`
                    }
                } else if (firstComponent && secondComponent) {
                    finalText = `${firstComponent} is functionally connected with the ${secondComponent}`
                }
                if (finalText) {
                    setSelectOptions((prevState) =>
                        prevState.concat([
                            {
                                label: `${finalText}`,
                                value: `functionalConnections_${val?.id}`,
                            },
                        ])
                    )
                }
            })
        }
    }, [form])
    useEffect(() => {
        const featuresPartsOfIndependentClaim = form.getFieldValue<
            MachineValues['featuresPartsOfIndependentClaim']
        >(['featuresPartsOfIndependentClaim'])
        if (!featuresPartsOfIndependentClaim && selectOptions.length > 0) {
            form.setFieldsValue({
                [name]: selectOptions.map((option: Option) => option.value),
            })
        }
    }, [form, name, selectOptions])
    return (
        <>
            <Form.Item name={name}>
                <Select
                    mode="multiple"
                    dropdownClassName="always-visible-scrollbar-dropdown"
                    options={selectOptions.map(({ value, label }: Option) => {
                        return {
                            value,
                            label,
                            key: value,
                        }
                    })}
                />
            </Form.Item>
        </>
    )
}

export default FeaturesPartsOfIndependentClaim
